import {defineSlotRecipe} from '@chakra-ui/react3'

/**
 * Recipe for the PDP product details accordion.
 *
 * `Accordion.Root` is rendered with `unstyled`, so the global accordion slot recipe
 * (`app/leap/theme/slot-recipes/accordion.js`) does not apply and this recipe has to
 * carry the layout of the parts as well, not just the visual overrides.
 */
export default defineSlotRecipe({
    slots: [
        'moduleContainer',
        'accordion',
        'item',
        'heading',
        'trigger',
        'itemHeadline',
        'indicator',
        'content',
        'contentInner'
    ],
    base: {
        // this lands on the inner `<section>` of `ModuleContainer`, which is capped at the
        // page max width and inset by the outer box' padding, so the horizontal padding
        // stays on that outer box (the default `modulePadding`).
        moduleContainer: {
            width: '100%',
            py: {base: 'spacing-48', s: 'spacing-80'},
            backgroundColor: 'primary.base.white' // the module is always white - so never use dark theme!
        },
        accordion: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
        },
        item: {
            overflowAnchor: 'none',
            borderTopWidth: '1px',
            borderColor: 'border.medium',
            color: 'text.primary',
            // the top border acts as the separator between items, so the first one opens
            // the list without a rule above it
            _first: {
                borderTopWidth: 0
            },
            _last: {
                borderBottomWidth: '1px'
            }
        },
        heading: {
            margin: 0
        },
        trigger: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 'spacing-16',
            width: '100%',
            py: 'spacing-32',
            px: 0,
            textAlign: 'start',
            cursor: 'pointer',
            outline: 0,
            _focusVisible: {
                outline: '2px solid',
                outlineColor: 'colors.blue.focusRing',
                outlineOffset: '-2px'
            }
        },
        itemHeadline: {
            textStyle: 'heading.large',
            color: 'text.primary'
        },
        indicator: {
            color: 'icons.primary',
            flexShrink: 0,
            transformOrigin: 'center',
            transition: 'rotate .2s',
            _open: {
                rotate: '-180deg'
            },
            _icon: {
                width: 5,
                height: 5
            }
        },
        content: {
            // to have the slider in spare parts spans the full viewport width
            overflowX: 'visible',
            _open: {
                animationName: 'expand-height, fade-in',
                animationDuration: 'moderate'
            },
            _closed: {
                animationName: 'collapse-height, fade-out',
                animationDuration: 'moderate'
            }
        },
        contentInner: {
            pb: 'spacing-32'
        }
    }
})
